/* tslint:disable */
/* eslint-disable */
/**
 * Tumelo API
 * This is Tumelo\'s API. It drives increased shareholder engagement by providing transparency of the organizations owned by investors and giving them a voice by allowing them to vote on important issues at these organizations.  The main uses of this API are:  * Transparency - To provide a breakdown to an investor of the organizations in which they are invested. * Voting - To provide the ability for an investor to vote on a poll concerning organizations in which they are invested.  ## Status  This API is currently available as a closed **BETA**. We invite interested parties to [give us feedback](mailto:support@tumelo.com) on this API, its documentation, or anything else about Tumelo\'s transparency service prior to the API becoming generally available.  ## Changelog  A changelog for the api can be found [here](CHANGELOG.md).  ## Access The API is available at the following URL: ``` https://api.prod.tumelo.com/v1 ```  For information on how to obtain access to the API, please [contact us](mailto:support@tumelo.com).  ## Development vs Production Habitats Development and Production environments are separated into their own habitats. Please [contact us](mailto:support@tumelo.com) when you\'re ready to go into production and require access to your company\'s live habitat.  # Using the API  ## Case Sensitivity URLs, headers, path parameters, query parameter names, and query parameter values are all case sensitive. Be careful to use the correct case when constructing requests.  ## Error Handling Requests may return an error indicating that there was a problem responding. The response will contain an HTTP status code as well as a JSON object in the following format which describes the error in more detail: ```json {   \"message\": \"Resource not found\" } ```  ## Pagination Requests that return lists and describe pagination parameters support paginated results. Results are separated into multiple pages, where the size of each page is determined by the `pageSize` query parameter. If omitted, `pageSize` defaults to 50 records. When a response includes a `nextPageToken` in the response body, this token value should be passed as a query parameter `nextPageToken` to the same endpoint in order to retrieve the next page of results. This process should be repeated until the response doesn’t include a `nextPageToken`, indicating that the end of the results has been reached.  When passing the `nextPageToken` parameter, all other filter and query parameters present in the previous request, **must** be filled. Filter parameters *must not* be changed while paginating since this will result in an error or an incorrect overall list. The `nextPageToken` will not be present in the response body if the end of the list has been reached and no further results are available.  ## String Formats The following string formats are used throughout this API.  ### date-time The valid format is date-time notation as defined by [RFC 3339, section 5.6](https://tools.ietf.org/html/rfc3339#section-5.6).  For example: `2020-11-30T10:45:28Z`  # Support If you have any technical questions relating to the Tumelo API, please [contact us](mailto:support@tumelo.com), explaining your question or issue in as much detail as possible, including the operation you were performing and the ISIN or Organization ID you were querying, as well as the results you expected and the actual results or error message you observed. You can expect a response within one business day.  ## Data Issues During development and testing in your development habitat we make available a test data set that includes some sample data constructed to illustrate functionality. It is not intended to be used for any purpose other than testing and/or demonstrating functionality, and should not be relied upon to be an accurate reflection of the underlying holdings of any real fund.  If you have any questions or concerns about the data accuracy or completeness of the data in the production environment, please [report the matter](mailto:support@tumelo.com) to us, giving as much information as possible about the issue.  # Compositions and Breakdowns  An instrument may have a composition, this means it is a fund invested in other instruments.  A breakdown is the bottom level components of a composite instrument or account. By this, we mean organizations and cash that a particular composite instrument or account is invested into either directly or through funds.  Breakdowns also have an `other` listing, this is for funds that only have partial information available, or where an instrument is not mapped to an organization. 
 *
 * The version of the OpenAPI document: 0.145.1
 * Contact: support@tumelo.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import * as runtime from '../runtime';
import type {
  FetchFundManagerPoliciesResponse,
  FundManager,
  FundManagerSignificantVote,
  FundManagerVote,
  ListFundManagerSignificantVotesResponse,
  ListFundManagerVotesResponse,
  ListFundManagersResponse,
  ListManagedInstrumentsResponse,
} from '../models';
import {
    FetchFundManagerPoliciesResponseFromJSON,
    FetchFundManagerPoliciesResponseToJSON,
    FundManagerFromJSON,
    FundManagerToJSON,
    FundManagerSignificantVoteFromJSON,
    FundManagerSignificantVoteToJSON,
    FundManagerVoteFromJSON,
    FundManagerVoteToJSON,
    ListFundManagerSignificantVotesResponseFromJSON,
    ListFundManagerSignificantVotesResponseToJSON,
    ListFundManagerVotesResponseFromJSON,
    ListFundManagerVotesResponseToJSON,
    ListFundManagersResponseFromJSON,
    ListFundManagersResponseToJSON,
    ListManagedInstrumentsResponseFromJSON,
    ListManagedInstrumentsResponseToJSON,
} from '../models';

export interface FetchFundManagerPoliciesRequest {
    fundManagerId: string;
}

export interface GetFundManagerRequest {
    fundManagerId: string;
}

export interface GetFundManagerSignificantVoteRequest {
    fundManagerId: string;
    isin: string;
    significantVoteId: string;
}

export interface GetFundManagerVoteRequest {
    fundManagerId: string;
    isin: string;
    voteId: string;
}

export interface ListFundManagerSignificantVotesRequest {
    fundManagerId: string;
    isin: string;
    pageSize?: number;
    nextPageToken?: string;
    createdBefore?: Date;
    createdAfter?: Date;
}

export interface ListFundManagerVotesRequest {
    fundManagerId: string;
    isin: string;
    pageSize?: number;
    nextPageToken?: string;
    createdBefore?: Date;
    createdAfter?: Date;
}

export interface ListFundManagersRequest {
    ids?: Array<string>;
    pageSize?: number;
    nextPageToken?: string;
}

export interface ListManagedInstrumentsRequest {
    fundManagerId: string;
    pageSize?: number;
    nextPageToken?: string;
}

/**
 * FundManagersApi - interface
 * 
 * @export
 * @interface FundManagersApiInterface
 */
export interface FundManagersApiInterface {
    /**
     * Returns the policies for this fund manager.
     * @summary Get fund manager policies
     * @param {string} fundManagerId An identifier of the fund manager for which policies are required.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof FundManagersApiInterface
     */
    fetchFundManagerPoliciesRaw(requestParameters: FetchFundManagerPoliciesRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<FetchFundManagerPoliciesResponse>>;

    /**
     * Returns the policies for this fund manager.
     * Get fund manager policies
     */
    fetchFundManagerPolicies(requestParameters: FetchFundManagerPoliciesRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<FetchFundManagerPoliciesResponse>;

    /**
     * Returns the fund manager based on its identifier.
     * @summary Get fund manager
     * @param {string} fundManagerId An identifier of the fund manager for which information is required.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof FundManagersApiInterface
     */
    getFundManagerRaw(requestParameters: GetFundManagerRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<FundManager>>;

    /**
     * Returns the fund manager based on its identifier.
     * Get fund manager
     */
    getFundManager(requestParameters: GetFundManagerRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<FundManager>;

    /**
     * Returns the fund manager significant vote for a given managed instrument based on its identifier.
     * @summary Get fund manager significant vote
     * @param {string} fundManagerId An identifier of the fund manager for which information is required.
     * @param {string} isin The ISIN of the managed instrument for which information is required.
     * @param {string} significantVoteId An identifier of the significant vote for which information is required.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof FundManagersApiInterface
     */
    getFundManagerSignificantVoteRaw(requestParameters: GetFundManagerSignificantVoteRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<FundManagerSignificantVote>>;

    /**
     * Returns the fund manager significant vote for a given managed instrument based on its identifier.
     * Get fund manager significant vote
     */
    getFundManagerSignificantVote(requestParameters: GetFundManagerSignificantVoteRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<FundManagerSignificantVote>;

    /**
     * Returns the fund manager vote for a given managed instrument based on its identifier.
     * @summary Get fund manager vote
     * @param {string} fundManagerId An identifier of the fund manager for which information is required.
     * @param {string} isin The ISIN of the managed instrument for which information is required.
     * @param {string} voteId An identifier of the vote for which information is required.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof FundManagersApiInterface
     */
    getFundManagerVoteRaw(requestParameters: GetFundManagerVoteRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<FundManagerVote>>;

    /**
     * Returns the fund manager vote for a given managed instrument based on its identifier.
     * Get fund manager vote
     */
    getFundManagerVote(requestParameters: GetFundManagerVoteRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<FundManagerVote>;

    /**
     * Returns a list of significant votes cast by a fund manager on behalf of a managed instrument. Without any pagination parameters, a maximum of 50 votes will be returned. 
     * @summary List significant votes cast by a fund manager for a specified managed instrument
     * @param {string} fundManagerId The fund manager ID.
     * @param {string} isin The ISIN of the managed instrument for which vote information is required.
     * @param {number} [pageSize] The maximum number of elements to return.
     * @param {string} [nextPageToken] Passing a nextPageToken will cause the next page of results to be retrieved.
     * @param {Date} [createdBefore] Passing a created before date will return votes that have a create time before the specified date.
     * @param {Date} [createdAfter] Passing a created after date will return votes that have a create time after the specified date.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof FundManagersApiInterface
     */
    listFundManagerSignificantVotesRaw(requestParameters: ListFundManagerSignificantVotesRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ListFundManagerSignificantVotesResponse>>;

    /**
     * Returns a list of significant votes cast by a fund manager on behalf of a managed instrument. Without any pagination parameters, a maximum of 50 votes will be returned. 
     * List significant votes cast by a fund manager for a specified managed instrument
     */
    listFundManagerSignificantVotes(requestParameters: ListFundManagerSignificantVotesRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ListFundManagerSignificantVotesResponse>;

    /**
     * Returns a list of votes cast by a fund manager on behalf of a managed isntrument. Without any pagination parameters, a maximum of 50 votes will be returned. 
     * @summary List votes cast by a fund manager for a specified managed instrument
     * @param {string} fundManagerId The fund manager ID.
     * @param {string} isin The ISIN of the managed instrument for which vote information is required.
     * @param {number} [pageSize] The maximum number of elements to return.
     * @param {string} [nextPageToken] Passing a nextPageToken will cause the next page of results to be retrieved.
     * @param {Date} [createdBefore] Passing a created before date will return votes that have a create time before the specified date.
     * @param {Date} [createdAfter] Passing a created after date will return votes that have a create time after the specified date.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof FundManagersApiInterface
     */
    listFundManagerVotesRaw(requestParameters: ListFundManagerVotesRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ListFundManagerVotesResponse>>;

    /**
     * Returns a list of votes cast by a fund manager on behalf of a managed isntrument. Without any pagination parameters, a maximum of 50 votes will be returned. 
     * List votes cast by a fund manager for a specified managed instrument
     */
    listFundManagerVotes(requestParameters: ListFundManagerVotesRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ListFundManagerVotesResponse>;

    /**
     * Returns a list of fund managers, including the information available on each one. Without any pagination parameters, a maximum of 50 fund managers will be returned.  If information is required for a list of specific fund managers, the `ids` query parameter may be used to define a filter for the response such that only fund managers matching the query parameter will be included. For example:      /fundManagers?ids=1cb935ce-b361-4a02-b68e-8a717e2fd653,c54ada83-a5fa-427f-ab57-3494582b534e  The request returns only fund managers for which a match can be found, and which the API client has access. Any duplicate fund managers are returned just once. The order of fund managers returned is not determined by the filters. 
     * @summary List fund managers
     * @param {Array<string>} [ids] A comma separated list of fund manager ids.
     * @param {number} [pageSize] The maximum number of elements to return.
     * @param {string} [nextPageToken] Passing a nextPageToken will cause the next page of results to be retrieved.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof FundManagersApiInterface
     */
    listFundManagersRaw(requestParameters: ListFundManagersRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ListFundManagersResponse>>;

    /**
     * Returns a list of fund managers, including the information available on each one. Without any pagination parameters, a maximum of 50 fund managers will be returned.  If information is required for a list of specific fund managers, the `ids` query parameter may be used to define a filter for the response such that only fund managers matching the query parameter will be included. For example:      /fundManagers?ids=1cb935ce-b361-4a02-b68e-8a717e2fd653,c54ada83-a5fa-427f-ab57-3494582b534e  The request returns only fund managers for which a match can be found, and which the API client has access. Any duplicate fund managers are returned just once. The order of fund managers returned is not determined by the filters. 
     * List fund managers
     */
    listFundManagers(requestParameters: ListFundManagersRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ListFundManagersResponse>;

    /**
     * Returns a fund manager\'s list of managed instrument ISINs. Without any pagination parameters, a maximum of 50 instruments will be returned. 
     * @summary List instruments managed by a fund manager
     * @param {string} fundManagerId The fund manager ID.
     * @param {number} [pageSize] The maximum number of elements to return.
     * @param {string} [nextPageToken] Passing a nextPageToken will cause the next page of results to be retrieved.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof FundManagersApiInterface
     */
    listManagedInstrumentsRaw(requestParameters: ListManagedInstrumentsRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ListManagedInstrumentsResponse>>;

    /**
     * Returns a fund manager\'s list of managed instrument ISINs. Without any pagination parameters, a maximum of 50 instruments will be returned. 
     * List instruments managed by a fund manager
     */
    listManagedInstruments(requestParameters: ListManagedInstrumentsRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ListManagedInstrumentsResponse>;

}

/**
 * 
 */
export class FundManagersApi extends runtime.BaseAPI implements FundManagersApiInterface {

    /**
     * Returns the policies for this fund manager.
     * Get fund manager policies
     */
    async fetchFundManagerPoliciesRaw(requestParameters: FetchFundManagerPoliciesRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<FetchFundManagerPoliciesResponse>> {
        if (requestParameters.fundManagerId === null || requestParameters.fundManagerId === undefined) {
            throw new runtime.RequiredError('fundManagerId','Required parameter requestParameters.fundManagerId was null or undefined when calling fetchFundManagerPolicies.');
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/fundManagers/{fundManagerId}/policies:fetch`.replace(`{${"fundManagerId"}}`, encodeURIComponent(String(requestParameters.fundManagerId))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => FetchFundManagerPoliciesResponseFromJSON(jsonValue));
    }

    /**
     * Returns the policies for this fund manager.
     * Get fund manager policies
     */
    async fetchFundManagerPolicies(requestParameters: FetchFundManagerPoliciesRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<FetchFundManagerPoliciesResponse> {
        const response = await this.fetchFundManagerPoliciesRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Returns the fund manager based on its identifier.
     * Get fund manager
     */
    async getFundManagerRaw(requestParameters: GetFundManagerRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<FundManager>> {
        if (requestParameters.fundManagerId === null || requestParameters.fundManagerId === undefined) {
            throw new runtime.RequiredError('fundManagerId','Required parameter requestParameters.fundManagerId was null or undefined when calling getFundManager.');
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/fundManagers/{fundManagerId}`.replace(`{${"fundManagerId"}}`, encodeURIComponent(String(requestParameters.fundManagerId))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => FundManagerFromJSON(jsonValue));
    }

    /**
     * Returns the fund manager based on its identifier.
     * Get fund manager
     */
    async getFundManager(requestParameters: GetFundManagerRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<FundManager> {
        const response = await this.getFundManagerRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Returns the fund manager significant vote for a given managed instrument based on its identifier.
     * Get fund manager significant vote
     */
    async getFundManagerSignificantVoteRaw(requestParameters: GetFundManagerSignificantVoteRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<FundManagerSignificantVote>> {
        if (requestParameters.fundManagerId === null || requestParameters.fundManagerId === undefined) {
            throw new runtime.RequiredError('fundManagerId','Required parameter requestParameters.fundManagerId was null or undefined when calling getFundManagerSignificantVote.');
        }

        if (requestParameters.isin === null || requestParameters.isin === undefined) {
            throw new runtime.RequiredError('isin','Required parameter requestParameters.isin was null or undefined when calling getFundManagerSignificantVote.');
        }

        if (requestParameters.significantVoteId === null || requestParameters.significantVoteId === undefined) {
            throw new runtime.RequiredError('significantVoteId','Required parameter requestParameters.significantVoteId was null or undefined when calling getFundManagerSignificantVote.');
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/fundManagers/{fundManagerId}/managedInstruments/{isin}/significantVotes/{significantVoteId}`.replace(`{${"fundManagerId"}}`, encodeURIComponent(String(requestParameters.fundManagerId))).replace(`{${"isin"}}`, encodeURIComponent(String(requestParameters.isin))).replace(`{${"significantVoteId"}}`, encodeURIComponent(String(requestParameters.significantVoteId))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => FundManagerSignificantVoteFromJSON(jsonValue));
    }

    /**
     * Returns the fund manager significant vote for a given managed instrument based on its identifier.
     * Get fund manager significant vote
     */
    async getFundManagerSignificantVote(requestParameters: GetFundManagerSignificantVoteRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<FundManagerSignificantVote> {
        const response = await this.getFundManagerSignificantVoteRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Returns the fund manager vote for a given managed instrument based on its identifier.
     * Get fund manager vote
     */
    async getFundManagerVoteRaw(requestParameters: GetFundManagerVoteRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<FundManagerVote>> {
        if (requestParameters.fundManagerId === null || requestParameters.fundManagerId === undefined) {
            throw new runtime.RequiredError('fundManagerId','Required parameter requestParameters.fundManagerId was null or undefined when calling getFundManagerVote.');
        }

        if (requestParameters.isin === null || requestParameters.isin === undefined) {
            throw new runtime.RequiredError('isin','Required parameter requestParameters.isin was null or undefined when calling getFundManagerVote.');
        }

        if (requestParameters.voteId === null || requestParameters.voteId === undefined) {
            throw new runtime.RequiredError('voteId','Required parameter requestParameters.voteId was null or undefined when calling getFundManagerVote.');
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/fundManagers/{fundManagerId}/managedInstruments/{isin}/votes/{voteId}`.replace(`{${"fundManagerId"}}`, encodeURIComponent(String(requestParameters.fundManagerId))).replace(`{${"isin"}}`, encodeURIComponent(String(requestParameters.isin))).replace(`{${"voteId"}}`, encodeURIComponent(String(requestParameters.voteId))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => FundManagerVoteFromJSON(jsonValue));
    }

    /**
     * Returns the fund manager vote for a given managed instrument based on its identifier.
     * Get fund manager vote
     */
    async getFundManagerVote(requestParameters: GetFundManagerVoteRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<FundManagerVote> {
        const response = await this.getFundManagerVoteRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Returns a list of significant votes cast by a fund manager on behalf of a managed instrument. Without any pagination parameters, a maximum of 50 votes will be returned. 
     * List significant votes cast by a fund manager for a specified managed instrument
     */
    async listFundManagerSignificantVotesRaw(requestParameters: ListFundManagerSignificantVotesRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ListFundManagerSignificantVotesResponse>> {
        if (requestParameters.fundManagerId === null || requestParameters.fundManagerId === undefined) {
            throw new runtime.RequiredError('fundManagerId','Required parameter requestParameters.fundManagerId was null or undefined when calling listFundManagerSignificantVotes.');
        }

        if (requestParameters.isin === null || requestParameters.isin === undefined) {
            throw new runtime.RequiredError('isin','Required parameter requestParameters.isin was null or undefined when calling listFundManagerSignificantVotes.');
        }

        const queryParameters: any = {};

        if (requestParameters.pageSize !== undefined) {
            queryParameters['pageSize'] = requestParameters.pageSize;
        }

        if (requestParameters.nextPageToken !== undefined) {
            queryParameters['nextPageToken'] = requestParameters.nextPageToken;
        }

        if (requestParameters.createdBefore !== undefined) {
            queryParameters['createdBefore'] = (requestParameters.createdBefore as any).toISOString();
        }

        if (requestParameters.createdAfter !== undefined) {
            queryParameters['createdAfter'] = (requestParameters.createdAfter as any).toISOString();
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/fundManagers/{fundManagerId}/managedInstruments/{isin}/significantVotes`.replace(`{${"fundManagerId"}}`, encodeURIComponent(String(requestParameters.fundManagerId))).replace(`{${"isin"}}`, encodeURIComponent(String(requestParameters.isin))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => ListFundManagerSignificantVotesResponseFromJSON(jsonValue));
    }

    /**
     * Returns a list of significant votes cast by a fund manager on behalf of a managed instrument. Without any pagination parameters, a maximum of 50 votes will be returned. 
     * List significant votes cast by a fund manager for a specified managed instrument
     */
    async listFundManagerSignificantVotes(requestParameters: ListFundManagerSignificantVotesRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ListFundManagerSignificantVotesResponse> {
        const response = await this.listFundManagerSignificantVotesRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Returns a list of votes cast by a fund manager on behalf of a managed isntrument. Without any pagination parameters, a maximum of 50 votes will be returned. 
     * List votes cast by a fund manager for a specified managed instrument
     */
    async listFundManagerVotesRaw(requestParameters: ListFundManagerVotesRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ListFundManagerVotesResponse>> {
        if (requestParameters.fundManagerId === null || requestParameters.fundManagerId === undefined) {
            throw new runtime.RequiredError('fundManagerId','Required parameter requestParameters.fundManagerId was null or undefined when calling listFundManagerVotes.');
        }

        if (requestParameters.isin === null || requestParameters.isin === undefined) {
            throw new runtime.RequiredError('isin','Required parameter requestParameters.isin was null or undefined when calling listFundManagerVotes.');
        }

        const queryParameters: any = {};

        if (requestParameters.pageSize !== undefined) {
            queryParameters['pageSize'] = requestParameters.pageSize;
        }

        if (requestParameters.nextPageToken !== undefined) {
            queryParameters['nextPageToken'] = requestParameters.nextPageToken;
        }

        if (requestParameters.createdBefore !== undefined) {
            queryParameters['createdBefore'] = (requestParameters.createdBefore as any).toISOString();
        }

        if (requestParameters.createdAfter !== undefined) {
            queryParameters['createdAfter'] = (requestParameters.createdAfter as any).toISOString();
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/fundManagers/{fundManagerId}/managedInstruments/{isin}/votes`.replace(`{${"fundManagerId"}}`, encodeURIComponent(String(requestParameters.fundManagerId))).replace(`{${"isin"}}`, encodeURIComponent(String(requestParameters.isin))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => ListFundManagerVotesResponseFromJSON(jsonValue));
    }

    /**
     * Returns a list of votes cast by a fund manager on behalf of a managed isntrument. Without any pagination parameters, a maximum of 50 votes will be returned. 
     * List votes cast by a fund manager for a specified managed instrument
     */
    async listFundManagerVotes(requestParameters: ListFundManagerVotesRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ListFundManagerVotesResponse> {
        const response = await this.listFundManagerVotesRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Returns a list of fund managers, including the information available on each one. Without any pagination parameters, a maximum of 50 fund managers will be returned.  If information is required for a list of specific fund managers, the `ids` query parameter may be used to define a filter for the response such that only fund managers matching the query parameter will be included. For example:      /fundManagers?ids=1cb935ce-b361-4a02-b68e-8a717e2fd653,c54ada83-a5fa-427f-ab57-3494582b534e  The request returns only fund managers for which a match can be found, and which the API client has access. Any duplicate fund managers are returned just once. The order of fund managers returned is not determined by the filters. 
     * List fund managers
     */
    async listFundManagersRaw(requestParameters: ListFundManagersRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ListFundManagersResponse>> {
        const queryParameters: any = {};

        if (requestParameters.ids) {
            queryParameters['ids'] = requestParameters.ids.join(runtime.COLLECTION_FORMATS["csv"]);
        }

        if (requestParameters.pageSize !== undefined) {
            queryParameters['pageSize'] = requestParameters.pageSize;
        }

        if (requestParameters.nextPageToken !== undefined) {
            queryParameters['nextPageToken'] = requestParameters.nextPageToken;
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/fundManagers`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => ListFundManagersResponseFromJSON(jsonValue));
    }

    /**
     * Returns a list of fund managers, including the information available on each one. Without any pagination parameters, a maximum of 50 fund managers will be returned.  If information is required for a list of specific fund managers, the `ids` query parameter may be used to define a filter for the response such that only fund managers matching the query parameter will be included. For example:      /fundManagers?ids=1cb935ce-b361-4a02-b68e-8a717e2fd653,c54ada83-a5fa-427f-ab57-3494582b534e  The request returns only fund managers for which a match can be found, and which the API client has access. Any duplicate fund managers are returned just once. The order of fund managers returned is not determined by the filters. 
     * List fund managers
     */
    async listFundManagers(requestParameters: ListFundManagersRequest = {}, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ListFundManagersResponse> {
        const response = await this.listFundManagersRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Returns a fund manager\'s list of managed instrument ISINs. Without any pagination parameters, a maximum of 50 instruments will be returned. 
     * List instruments managed by a fund manager
     */
    async listManagedInstrumentsRaw(requestParameters: ListManagedInstrumentsRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ListManagedInstrumentsResponse>> {
        if (requestParameters.fundManagerId === null || requestParameters.fundManagerId === undefined) {
            throw new runtime.RequiredError('fundManagerId','Required parameter requestParameters.fundManagerId was null or undefined when calling listManagedInstruments.');
        }

        const queryParameters: any = {};

        if (requestParameters.pageSize !== undefined) {
            queryParameters['pageSize'] = requestParameters.pageSize;
        }

        if (requestParameters.nextPageToken !== undefined) {
            queryParameters['nextPageToken'] = requestParameters.nextPageToken;
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/fundManagers/{fundManagerId}/managedInstruments`.replace(`{${"fundManagerId"}}`, encodeURIComponent(String(requestParameters.fundManagerId))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => ListManagedInstrumentsResponseFromJSON(jsonValue));
    }

    /**
     * Returns a fund manager\'s list of managed instrument ISINs. Without any pagination parameters, a maximum of 50 instruments will be returned. 
     * List instruments managed by a fund manager
     */
    async listManagedInstruments(requestParameters: ListManagedInstrumentsRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ListManagedInstrumentsResponse> {
        const response = await this.listManagedInstrumentsRaw(requestParameters, initOverrides);
        return await response.value();
    }

}
